import React, { createContext, useState, useMemo, useContext, useEffect, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { unifiedThemes, DEFAULT_THEME, getSystemAppropriateTheme } from '../themes';
import { applyThemeVariables } from '../themes/utils/cssVariables';
import { createChatColorTokens } from '../themes/tokens/chatColorTokens';
import { createFileIconTokens } from '../themes/tokens/fileIconTokens';
import { useSystemColorScheme } from '../hooks/useSystemColorScheme';

const THEME_PAIRS = {
  'aiflock':           { light: 'aiflock', dark: 'aiflock-dark' },
  'aiflock-dark':      { light: 'aiflock', dark: 'aiflock-dark' },
  'google-opal-light': { light: 'google-opal-light', dark: 'google-opal-dark' },
  'google-opal-dark':  { light: 'google-opal-light', dark: 'google-opal-dark' },
  'corporate':         { light: 'corporate', dark: 'corporate' },
  'high-contrast':     { light: 'high-contrast', dark: 'high-contrast' },
};

// 1. Crear el Contexto
const ThemeContext = createContext();

// 2. Crear el Componente Proveedor - EVOLUCIÓN INTELIGENTE
export function CustomThemeProvider({ children }) {
  const systemScheme = useSystemColorScheme();

  // ✅ MANTENER: Compatibilidad con toggle actual
  const [mode, setMode] = useState(() => {
    const storedMode = localStorage.getItem('theme');
    return storedMode === 'dark' ? 'dark' : 'light';
  });

  // 🆕 AÑADIR: Sistema de temas completos
  const [activeTheme, setActiveTheme] = useState(() => {
    const colorSchemeMode = localStorage.getItem('theme-mode') || 'auto';
    if (colorSchemeMode === 'auto') {
      return getSystemAppropriateTheme(DEFAULT_THEME);
    }
    const savedTheme = localStorage.getItem('activeTheme');
    if (savedTheme && unifiedThemes[savedTheme]) {
      return savedTheme;
    }
    return getSystemAppropriateTheme(DEFAULT_THEME);
  });

  const [colorSchemeMode, setColorSchemeModeState] = useState(
    () => localStorage.getItem('theme-mode') || 'auto'
  );

  // Auto-switch theme variant when system color scheme changes
  useEffect(() => {
    if (colorSchemeMode !== 'auto') return;
    const pair = THEME_PAIRS[activeTheme];
    if (!pair) return;
    const target = systemScheme === 'dark' ? pair.dark : pair.light;
    if (target !== activeTheme && unifiedThemes[target]) {
      setActiveTheme(target);
      localStorage.setItem('activeTheme', target);
      const newMode = unifiedThemes[target].mode;
      setMode(newMode);
      localStorage.setItem('theme', newMode);
    }
  }, [systemScheme, colorSchemeMode, activeTheme]);

  // 🆕 GENERAR: Tema MUI dinámico basado en tema activo
  const muiTheme = useMemo(() => {
    const currentThemeConfig = unifiedThemes[activeTheme];
    
    if (!currentThemeConfig) {
      console.warn(`[ThemeContext] Tema no encontrado: ${activeTheme}, usando fallback`);
      const fallbackConfig = unifiedThemes[DEFAULT_THEME];
      return createTheme(fallbackConfig);
    }

    // Crear tema MUI usando la configuración unificada
    const themeConfig = {
      palette: {
        mode: currentThemeConfig.mode,
        ...currentThemeConfig.palette
      },
      typography: currentThemeConfig.typography || {},
      breakpoints: {
        values: currentThemeConfig.breakpoints || {}
      },
      sections: currentThemeConfig.sections || {},  // ⭐ CRÍTICO: Incluir sections para chat colors
      components: {
        // Mantener scrollbar styles del tema original
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              fontFamily: currentThemeConfig.typography?.fontFamily || '"Inter", "Roboto", sans-serif',
              ...currentThemeConfig.components?.MuiCssBaseline?.styleOverrides?.body
            },
            '*::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '*::-webkit-scrollbar-track': {
              background: currentThemeConfig.mode === 'light' ? '#f1f1f1' : '#262728',
            },
            '*::-webkit-scrollbar-thumb': {
              background: currentThemeConfig.mode === 'light' ? '#888' : '#4a4b4c',
              borderRadius: '4px',
            },
            '*::-webkit-scrollbar-thumb:hover': {
              background: currentThemeConfig.mode === 'light' ? '#555' : '#5a5b5c',
            },
          },
        },
        // Merge con los componentes del tema
        ...currentThemeConfig.components
      }
    };

    const theme = createTheme(themeConfig);

    // Add semantic color tokens to theme
    theme.chat = createChatColorTokens(theme);
    theme.fileIcons = createFileIconTokens(theme);

    return theme;
  }, [activeTheme]);

  // ✅ MANTENER: Toggle existente (actualiza 'mode' y 'activeTheme' si es necesario)
  // ✅ FIX: Wrapped in useCallback to prevent recreation on every render
  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newMode);
      // Exit auto mode when user manually toggles
      setColorSchemeModeState(newMode);
      localStorage.setItem('theme-mode', newMode);

      // Si el tema activo es uno de los "google-opal", cambiar también su variante
      setActiveTheme((currentTheme) => {
        if (currentTheme.startsWith('google-opal')) {
          const newTheme = newMode === 'light' ? 'google-opal-light' : 'google-opal-dark';
          localStorage.setItem('activeTheme', newTheme);
          return newTheme;
        }
        // Si el tema activo es "aiflock", cambiar también su variante
        else if (currentTheme === 'aiflock' || currentTheme === 'aiflock-dark') {
          const newTheme = newMode === 'light' ? 'aiflock' : 'aiflock-dark';
          localStorage.setItem('activeTheme', newTheme);
          return newTheme;
        }
        return currentTheme;
      });

      return newMode;
    });
  }, []); // Empty deps - uses functional setState to avoid stale closures

  // 🆕 AÑADIR: Función para cambiar el tema completo
  // ✅ FIX: Wrapped in useCallback to prevent recreation on every render
  const switchTheme = useCallback((themeName) => {
    if (!unifiedThemes[themeName]) {
      console.warn(`[ThemeContext] Tema no válido: ${themeName}`);
      return;
    }

    setActiveTheme(themeName);
    localStorage.setItem('activeTheme', themeName);

    // Exit auto mode when user explicitly picks a theme
    const newThemeConfig = unifiedThemes[themeName];
    setColorSchemeModeState(newThemeConfig.mode);
    localStorage.setItem('theme-mode', newThemeConfig.mode);

    // Actualizar el modo global si el nuevo tema tiene un modo específico
    setMode((currentMode) => {
      if (newThemeConfig.mode !== currentMode) {
        localStorage.setItem('theme', newThemeConfig.mode);
        return newThemeConfig.mode;
      }
      return currentMode;
    });
  }, []); // Empty deps - uses functional setState to avoid stale closures

  const changeColorSchemeMode = useCallback((newColorSchemeMode) => {
    setColorSchemeModeState(newColorSchemeMode);
    localStorage.setItem('theme-mode', newColorSchemeMode);

    const applyVariant = (schemeTarget) => {
      setActiveTheme((prev) => {
        const pair = THEME_PAIRS[prev];
        if (!pair) return prev;
        const target = schemeTarget === 'dark' ? pair.dark : pair.light;
        if (target !== prev && unifiedThemes[target]) {
          localStorage.setItem('activeTheme', target);
          const tMode = unifiedThemes[target].mode;
          setMode(tMode);
          localStorage.setItem('theme', tMode);
          return target;
        }
        return prev;
      });
    };

    if (newColorSchemeMode === 'auto') {
      const sysScheme = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      applyVariant(sysScheme);
    } else {
      applyVariant(newColorSchemeMode);
    }
  }, []);

  // 🆕 Aplicar variables CSS del tema activo al cargar y al cambiar
  useEffect(() => {
    if (unifiedThemes[activeTheme]) {
      applyThemeVariables(unifiedThemes[activeTheme]);
      console.log(`🎨 [ThemeContext] Tema aplicado: ${activeTheme}`);
    }
  }, [activeTheme]);

  // 🆕 Aplicar data-theme attribute al HTML para Mermaid diagrams
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  // ✅ EXTENDER: Valor del contexto con nueva funcionalidad
  const value = useMemo(() => ({
    // Funcionalidad existente - 100% compatible
    mode,
    toggleTheme,

    // Nueva funcionalidad de temas unificados
    activeTheme,
    themeConfig: unifiedThemes[activeTheme],
    availableThemes: unifiedThemes,
    switchTheme,

    // Color scheme mode (auto/light/dark)
    colorSchemeMode,
    changeColorSchemeMode,
  }), [mode, activeTheme, toggleTheme, switchTheme, colorSchemeMode, changeColorSchemeMode]);

  return (
    <ThemeContext.Provider value={value}>
      {/* 🎨 Sistema de temas unificado con MUI */}
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

// 4. Crear y exportar un custom hook para consumir el contexto fácilmente
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a CustomThemeProvider');
  }
  return context;
}; 